import React, { useState, useEffect } from "react";
import { sidebarMenuService } from "../services/sidebarMenuService";
import { adminService } from "../services/adminService";
import { permissionService } from "../services/permissionService";
// Simple icon components
const IconPlus = () => <span className="text-lg">+</span>;
const IconEdit = () => <span className="text-lg">✏️</span>;
const IconTrash = () => <span className="text-lg">🗑️</span>;
const IconSave = () => <span className="text-lg">💾</span>;
const IconTimes = () => <span className="text-lg">✕</span>;
const IconList = () => <span className="text-lg">📋</span>;
const IconUserShield = () => <span className="text-lg">👤</span>;

const SidebarMenuManagement = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [roles, setRoles] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showMenuForm, setShowMenuForm] = useState(false);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [groupByRole, setGroupByRole] = useState(true);
  const [menuFormData, setMenuFormData] = useState({
    title: "",
    icon: "",
    route: "",
    order_index: 0,
    parent_id: null,
    is_active: 1,
  });
  const [assignFormData, setAssignFormData] = useState({
    menuItemIds: [],
    roleId: "",
    adminId: "",
  });
  const [currentAssignments, setCurrentAssignments] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [menuRes, rolesRes, adminsRes, assignmentsRes] = await Promise.all([
        sidebarMenuService.getAllMenuItems(),
        permissionService.getRoles(),
        adminService.getAllAdmins(),
        sidebarMenuService.getAllAssignments(),
      ]);

      setMenuItems(menuRes.data.data || []);
      setRoles(rolesRes.data.data || []);
      setAdmins(adminsRes.data.data || []);
      setAssignments(assignmentsRes.data.data || []);
    } catch (error) {
      console.error("Error loading data:", error);
      alert("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleMenuSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...menuFormData };
      // "Last" = place at end: parent_id null, order_index = max + 1
      if (payload.parent_id === "last") {
        const maxOrder = menuItems.length
          ? Math.max(...menuItems.map((m) => m.order_index ?? 0))
          : -1;
        payload.parent_id = null;
        payload.order_index = maxOrder + 1;
      } else if (payload.parent_id === "") {
        payload.parent_id = null;
      }
      if (editingItem) {
        await sidebarMenuService.updateMenuItem(editingItem.id, payload);
        alert("Menu item updated successfully");
      } else {
        await sidebarMenuService.createMenuItem(payload);
        alert("Menu item created successfully");
      }
      setShowMenuForm(false);
      setEditingItem(null);
      setMenuFormData({
        title: "",
        icon: "",
        route: "",
        order_index: 0,
        parent_id: null,
        is_active: 1,
      });
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || "Failed to save menu item");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this menu item?")) {
      return;
    }
    setLoading(true);
    try {
      await sidebarMenuService.deleteMenuItem(id);
      alert("Menu item deleted successfully");
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || "Failed to delete menu item");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm("Are you sure you want to remove this assignment?")) {
      return;
    }
    setLoading(true);
    try {
      await sidebarMenuService.deleteAssignment(assignmentId);
      alert("Assignment removed successfully");
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || "Failed to remove assignment");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setMenuFormData({
      title: item.title,
      icon: item.icon || "",
      route: item.route,
      order_index: item.order_index,
      parent_id: item.parent_id || null,
      is_active: item.is_active,
    });
    setShowMenuForm(true);
  };

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    if (assignFormData.menuItemIds.length === 0) {
      alert("Please select at least one menu item");
      return;
    }
    if (!assignFormData.roleId && !assignFormData.adminId) {
      alert("Please select either a role or an admin");
      return;
    }

    setLoading(true);
    try {
      await sidebarMenuService.assignMenuItems(assignFormData);
      alert("Menu items assigned successfully");
      setShowAssignForm(false);
      setAssignFormData({
        menuItemIds: [],
        roleId: "",
        adminId: "",
      });
      setCurrentAssignments([]);
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || "Failed to assign menu items");
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentAssignments = async (roleId, adminId) => {
    if (!roleId && !adminId) {
      setCurrentAssignments([]);
      setAssignFormData((prev) => ({ ...prev, menuItemIds: [] }));
      return;
    }

    try {
      const response = await sidebarMenuService.getAssignedMenuItems(
        roleId,
        adminId
      );
      if (response.data && response.data.success && response.data.data) {
        const assignedIds = response.data.data.map((item) => item.id);
        setCurrentAssignments(assignedIds);
        setAssignFormData((prev) => ({ ...prev, menuItemIds: assignedIds }));
      }
    } catch (error) {
      console.error("Error loading current assignments:", error);
      setCurrentAssignments([]);
      setAssignFormData((prev) => ({ ...prev, menuItemIds: [] }));
    }
  };

  const handleRoleChange = (roleId) => {
    setAssignFormData({
      ...assignFormData,
      roleId: roleId,
      adminId: "", // Clear admin when role is selected
    });
    if (roleId) {
      loadCurrentAssignments(roleId, null);
    } else {
      setCurrentAssignments([]);
      setAssignFormData((prev) => ({ ...prev, menuItemIds: [] }));
    }
  };

  const handleAdminChange = (adminId) => {
    setAssignFormData({
      ...assignFormData,
      adminId: adminId,
      roleId: "", // Clear role when admin is selected
    });
    if (adminId) {
      loadCurrentAssignments(null, adminId);
    } else {
      setCurrentAssignments([]);
      setAssignFormData((prev) => ({ ...prev, menuItemIds: [] }));
    }
  };

  const toggleMenuItemSelection = (itemId) => {
    setAssignFormData((prev) => {
      const isSelected = prev.menuItemIds.includes(itemId);
      return {
        ...prev,
        menuItemIds: isSelected
          ? prev.menuItemIds.filter((id) => id !== itemId)
          : [...prev.menuItemIds, itemId],
      };
    });
  };

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Sidebar Menu Management</h2>
          <div className="flex gap-2">
            <button
              onClick={() => {
                setShowAssignForm(true);
                setShowMenuForm(false);
              }}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2"
            >
              <IconUserShield /> Assign Menu Items
            </button>
            <button
              onClick={() => {
                setShowMenuForm(true);
                setShowAssignForm(false);
                setEditingItem(null);
                setMenuFormData({
                  title: "",
                  icon: "",
                  route: "",
                  order_index: 0,
                  parent_id: null,
                  is_active: 1,
                });
              }}
              className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 flex items-center gap-2"
            >
              <IconPlus /> Add Menu Item
            </button>
          </div>
        </div>

        {/* Menu Item Form */}
        {showMenuForm && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 rounded-lg shadow-lg">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">
                  {editingItem ? "Edit Menu Item" : "Add New Menu Item"}
                </h3>
                <button
                  onClick={() => {
                    setShowMenuForm(false);
                    setEditingItem(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <IconTimes />
                </button>
              </div>
              <form onSubmit={handleMenuSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={menuFormData.title}
                      onChange={(e) =>
                        setMenuFormData({
                          ...menuFormData,
                          title: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2 border rounded-md"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Icon (e.g., FaHome)
                    </label>
                    <input
                      type="text"
                      value={menuFormData.icon}
                      onChange={(e) =>
                        setMenuFormData({
                          ...menuFormData,
                          icon: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2 border rounded-md"
                      placeholder="FaHome"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Route *
                    </label>
                    <input
                      type="text"
                      value={menuFormData.route}
                      onChange={(e) =>
                        setMenuFormData({
                          ...menuFormData,
                          route: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2 border rounded-md"
                      required
                      placeholder="/admin/dashboard"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Order Index
                    </label>
                    <input
                      type="number"
                      value={menuFormData.order_index}
                      onChange={(e) =>
                        setMenuFormData({
                          ...menuFormData,
                          order_index: parseInt(e.target.value) || 0,
                        })
                      }
                      className="w-full px-4 py-2 border rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Parent Menu (Optional)
                    </label>
                    <select
                      value={
                        menuFormData.parent_id === null ||
                        menuFormData.parent_id === undefined
                          ? ""
                          : menuFormData.parent_id
                      }
                      onChange={(e) => {
                        const v = e.target.value;
                        setMenuFormData({
                          ...menuFormData,
                          parent_id:
                            v === ""
                              ? null
                              : v === "last"
                              ? "last"
                              : parseInt(v, 10),
                        });
                      }}
                      className="w-full px-4 py-2 border rounded-md"
                    >
                      <option value="">None (Top Level)</option>
                      {menuItems
                        .filter((item) => item.id !== editingItem?.id)
                        .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
                        .map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.title}
                          </option>
                        ))}
                      <option value="last">Last</option>
                    </select>
                    {menuFormData.parent_id === "last" && (
                      <p className="text-xs text-gray-500 mt-1">
                        This item will appear at the end of the menu.
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Status
                    </label>
                    <select
                      value={menuFormData.is_active}
                      onChange={(e) =>
                        setMenuFormData({
                          ...menuFormData,
                          is_active: parseInt(e.target.value),
                        })
                      }
                      className="w-full px-4 py-2 border rounded-md"
                    >
                      <option value={1}>Active</option>
                      <option value={0}>Inactive</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    <IconSave /> {editingItem ? "Update" : "Create"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowMenuForm(false);
                      setEditingItem(null);
                    }}
                    className="bg-gray-500 text-white px-6 py-2 rounded-md hover:bg-gray-600 flex items-center gap-2"
                  >
                    <IconTimes /> Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Assignment Form */}
        {showAssignForm && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 rounded-lg shadow-lg">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">Assign Menu Items</h3>
                <button
                  onClick={() => {
                    setShowAssignForm(false);
                    setAssignFormData({
                      menuItemIds: [],
                      roleId: "",
                      adminId: "",
                    });
                    setCurrentAssignments([]);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <IconTimes />
                </button>
              </div>
              <form onSubmit={handleAssignSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Assign to Role
                    </label>
                    <select
                      value={assignFormData.roleId}
                      onChange={(e) => handleRoleChange(e.target.value)}
                      className="w-full px-4 py-2 border rounded-md"
                    >
                      <option value="">Select Role</option>
                      {roles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.role_name}
                        </option>
                      ))}
                    </select>
                    {assignFormData.roleId && currentAssignments.length > 0 && (
                      <p className="text-xs text-blue-600 mt-1">
                        {currentAssignments.length} menu item(s) already
                        assigned
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      OR Assign to Admin
                    </label>
                    <select
                      value={assignFormData.adminId}
                      onChange={(e) => handleAdminChange(e.target.value)}
                      className="w-full px-4 py-2 border rounded-md"
                    >
                      <option value="">Select Admin</option>
                      {admins.map((admin) => (
                        <option key={admin.id} value={admin.id}>
                          {admin.first_name} {admin.last_name} ({admin.role})
                        </option>
                      ))}
                    </select>
                    {assignFormData.adminId &&
                      currentAssignments.length > 0 && (
                        <p className="text-xs text-blue-600 mt-1">
                          {currentAssignments.length} menu item(s) already
                          assigned
                        </p>
                      )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Select Menu Items ({assignFormData.menuItemIds.length}{" "}
                    selected)
                    {(assignFormData.roleId || assignFormData.adminId) && (
                      <span className="ml-2 text-xs text-gray-500">
                        ({currentAssignments.length} already assigned)
                      </span>
                    )}
                  </label>
                  <div className="border rounded-md p-4 max-h-60 overflow-y-auto">
                    {menuItems
                      .filter((item) => item.is_active)
                      .map((item) => {
                        const isAssigned = currentAssignments.includes(item.id);
                        const isSelected = assignFormData.menuItemIds.includes(
                          item.id
                        );
                        return (
                          <label
                            key={item.id}
                            className={`flex items-center gap-2 p-2 hover:bg-gray-50 cursor-pointer ${
                              isAssigned ? "bg-blue-50" : ""
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleMenuItemSelection(item.id)}
                              className="rounded"
                            />
                            <span className="flex-1">{item.title}</span>
                            <span className="text-gray-500 text-sm font-mono">
                              ({item.route})
                            </span>
                            {isAssigned && (
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                Assigned
                              </span>
                            )}
                          </label>
                        );
                      })}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    <IconSave /> Assign
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAssignForm(false);
                      setAssignFormData({
                        menuItemIds: [],
                        roleId: "",
                        adminId: "",
                      });
                    }}
                    className="bg-gray-500 text-white px-6 py-2 rounded-md hover:bg-gray-600 flex items-center gap-2"
                  >
                    <IconTimes /> Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Menu Items List */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
          <div className="px-6 py-4 border-b bg-blue-700 text-white flex items-center gap-2">
            <h3 className="text-lg font-bold flex items-center gap-2 ">
              <IconList /> Menu Items ({menuItems.length})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-teal-600 to-emerald-600">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase">
                    Icon
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase">
                    Route
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase">
                    Order
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {menuItems
                  .sort((a, b) => a.order_index - b.order_index)
                  .map((item) => {
                    const isAssigned = assignments.some(
                      (assign) => assign.menu_item_id === item.id
                    );
                    return (
                      <tr
                        key={item.id}
                        className={!isAssigned ? "bg-yellow-50" : ""}
                      >
                        <td className="px-6 py-4 whitespace-nowrap font-medium">
                          {item.title}
                          {!isAssigned && (
                            <span className="ml-2 text-xs text-yellow-600 bg-yellow-100 px-2 py-1 rounded">
                              Unassigned
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                          {item.icon || "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-600 font-mono text-sm">
                              {item.route}
                            </span>
                            <button
                              onClick={() => handleEdit(item)}
                              className="text-blue-600 hover:text-blue-900 text-xs"
                              title="Edit Route"
                            >
                              ✏️
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {item.order_index}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 rounded-full text-xs ${
                              item.is_active
                                ? "bg-blue-100 text-blue-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {item.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEdit(item)}
                              className="text-blue-600 hover:text-blue-900 p-2 rounded hover:bg-blue-50 transition-colors"
                              title="Edit"
                            >
                              <IconEdit />
                            </button>
                            <button
                              onClick={() => handleDelete(item.id)}
                              className="text-red-600 hover:text-red-900 p-2 rounded hover:bg-red-50 transition-colors"
                              title="Delete"
                            >
                              <IconTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Assignments List */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b bg-gray-50 flex justify-between items-center bg-blue-700 text-white">
            <h3 className="text-lg font-bold flex items-center gap-2">
              <IconUserShield /> Current Assignments ({assignments.length})
            </h3>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={groupByRole}
                onChange={(e) => setGroupByRole(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm text-gray-700">Group by Role</span>
            </label>
          </div>
          <div className="overflow-x-auto">
            {groupByRole ? (
              // Grouped by Role view
              <div className="divide-y divide-gray-200">
                {(() => {
                  const roleGroups = {};
                  assignments.forEach((assignment) => {
                    const key =
                      assignment.role_name || `admin_${assignment.admin_id}`;
                    if (!roleGroups[key]) {
                      roleGroups[key] = {
                        name:
                          assignment.role_name ||
                          `Admin: ${assignment.admin_first_name} ${assignment.admin_last_name}`,
                        isRole: !!assignment.role_name,
                        assignments: [],
                      };
                    }
                    roleGroups[key].assignments.push(assignment);
                  });
                  return Object.entries(roleGroups).map(([key, group]) => (
                    <div key={key} className="p-4">
                      <h4 className="font-bold text-md mb-3 flex items-center gap-2">
                        <span
                          className={`px-3 py-1 rounded text-sm ${
                            group.isRole
                              ? "bg-blue-100 text-blue-800"
                              : "bg-blue-100 text-blue-800"
                          }`}
                        >
                          {group.name}
                        </span>
                        <span className="text-gray-500 text-sm">
                          ({group.assignments.length} menu items)
                        </span>
                      </h4>
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gradient-to-r from-teal-600 to-emerald-600 rounded-t-lg">
                          <tr>
                            <th className="px-6 py-2 text-left text-xs font-medium text-white uppercase">
                              Menu Item
                            </th>
                            <th className="px-6 py-2 text-left text-xs font-medium text-white uppercase">
                              Route
                            </th>
                            <th className="px-6 py-2 text-left text-xs font-medium text-white uppercase">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {group.assignments.map((assignment) => {
                            const menuItem = menuItems.find(
                              (item) => item.id === assignment.menu_item_id
                            );
                            return (
                              <tr key={assignment.id}>
                                <td className="px-6 py-3 whitespace-nowrap font-medium">
                                  {assignment.menu_title}
                                  <button
                                    onClick={() =>
                                      menuItem && handleEdit(menuItem)
                                    }
                                    className="ml-2 text-blue-600 hover:text-blue-900 text-xs"
                                    title="Edit Menu Item"
                                  >
                                    ✏️
                                  </button>
                                </td>
                                <td className="px-6 py-3 whitespace-nowrap text-gray-600 font-mono text-sm">
                                  {assignment.menu_route}
                                </td>
                                <td className="px-6 py-3 whitespace-nowrap">
                                  <button
                                    onClick={() =>
                                      handleDeleteAssignment(assignment.id)
                                    }
                                    className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-colors"
                                    title="Remove Assignment"
                                  >
                                    <IconTrash />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ));
                })()}
              </div>
            ) : (
              // Flat list view
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Menu Item
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Route
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Assigned To
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {assignments.map((assignment) => {
                    const menuItem = menuItems.find(
                      (item) => item.id === assignment.menu_item_id
                    );
                    return (
                      <tr key={assignment.id}>
                        <td className="px-6 py-4 whitespace-nowrap font-medium">
                          {assignment.menu_title}
                          {menuItem && (
                            <button
                              onClick={() => handleEdit(menuItem)}
                              className="ml-2 text-blue-600 hover:text-blue-900 text-xs"
                              title="Edit Menu Item"
                            >
                              ✏️
                            </button>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-600 font-mono text-sm">
                          {assignment.menu_route}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {assignment.role_name ? (
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                              Role: {assignment.role_name}
                            </span>
                          ) : assignment.admin_first_name ? (
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                              Admin: {assignment.admin_first_name}{" "}
                              {assignment.admin_last_name}
                            </span>
                          ) : (
                            <span className="text-gray-500">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() =>
                              handleDeleteAssignment(assignment.id)
                            }
                            className="text-red-600 hover:text-red-900 p-2 rounded hover:bg-red-50 transition-colors"
                            title="Remove Assignment"
                          >
                            <IconTrash />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SidebarMenuManagement;
